import { Button, Card, CardSection, Loader } from "cxa-components";
import React, { Fragment, useEffect, useState } from "react";
import { injectIntl, intlShape } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { getECardsData, setSelectedEcards } from "../../actions/claimActionsPP";
import { ClickOutside } from "../common/ClickOutsideWrapper";
import messages from "../messagesDescription";
import Modal from "react-modal";
import PropTypes from "prop-types";
import hasMenuWithId from "../common/HasMenuWithId";
import ECardsItemLayout from "../common/ECardsLayout";
import TrackSsoUrlComponent from "components/common/TrackSsoUrlComponent";
import { getShortLivedToken } from "actions/loginActions";
import html2canvas from "html2canvas";
import CustomDownloadEcardLayout from "./CustomDownloadEcardLayout";

const ECards = ({ intl, history }) => {
  const { eCardsList, isFetching } = useSelector(state => state.claimsPP);
  const menuItems = useSelector(state => state.common.menuItems);
  const dispatch = useDispatch();
  const [selectedCardFilter, setSelectedCardFilter] = useState("All");
  const [selectedMemberFilter, setSelectedMemberFilter] = useState("All");
  const [filteredCards, setFilteredCards] = useState(eCardsList);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [downloadItem, setDownloadItem] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const fetchShortLivedToken = () => dispatch(getShortLivedToken());

  useEffect(() => {
    if (!eCardsList.length) {
      dispatch(getECardsData());
    }
  }, [dispatch, eCardsList.length]);

  useEffect(() => {
    if (eCardsList.length) {
      setFilteredCards(eCardsList);
    }
  }, [eCardsList]);

  if (isFetching && !eCardsList.length) return <Loader />;

  // handle duplicate providerName
  const uniqueProviders = Array.from(
    new Map(eCardsList.map(card => [card.providerName || card.providerId, card])).values()
  );

  // filters for all cards
  const intlAllCards = intl.formatMessage(messages.claims.eCardsProviderFilter);

  const allCards =
    typeof intlAllCards === "string" ? { value: "All", label: intlAllCards } : intlAllCards;

  const filters = [
    allCards,
    ...uniqueProviders.map(card => {
      const provider = card.providerName || card.providerId;
      return { value: provider, label: provider };
    }),
  ];

  // handle duplicate memberName
  const uniqueMembers = Array.from(
    new Map(eCardsList.map(card => [card.memberName, card])).values()
  );

  // filters for all members
  const intlAllMembers = intl.formatMessage(messages.claims.eCardsMemberFilter);
  const allMembers =
    typeof intlAllMembers === "string" ? { value: "All", label: intlAllMembers } : intlAllMembers;

  const filterMembers = [
    allMembers,
    ...uniqueMembers.map(card => ({
      value: card.memberName,
      label: card.memberName,
    })),
  ];

  const handleDownload = async item => {
    setDownloadLoading(true);
    const hasFrontImage = item.eCardFrontImageURL && item.eCardFrontImageURL.trim() !== "";
    const hasBackImage = item.eCardBackImageURL && item.eCardBackImageURL.trim() !== "";

    if (!hasFrontImage && !hasBackImage) {
      alert(intl.formatMessage(messages.claims.noImageECardsDownload));
      return;
    }

    if (hasFrontImage && hasBackImage) {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      const frontImage = await loadImage(item.eCardFrontImageURL);
      const backImage = await loadImage(item.eCardBackImageURL);

      const width = Math.max(frontImage.width, backImage.width);
      const height = frontImage.height + backImage.height;
      canvas.width = width;
      canvas.height = height;

      context.drawImage(frontImage, 0, 0);
      context.drawImage(backImage, 0, frontImage.height);

      const combinedImageURL = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.href = combinedImageURL;
      link.download = `${item.providerId || "eCard"}.png`;
      link.click();
      setDownloadLoading(false);
      setDownloadItem(null);
      setModalIsOpen(false);
    } else {
      const imageURL = hasFrontImage ? item.eCardFrontImageURL : item.eCardBackImageURL;

      const link = document.createElement("a");
      link.href = imageURL;
      link.download = `${item.providerId || "eCard"}.png`;
      link.click();
      setDownloadLoading(false);
      setDownloadItem(null);
      setModalIsOpen(false);
    }
  };

  const handleCustomDownload = async value => {
    const cardElement = document.getElementById(`ecard-${value.cardId}`);

    if (!cardElement) {
      alert("ECards not found!.");
      return;
    }

    const originalStyle = cardElement.style.display;

    try {
      setDownloadLoading(true);

      cardElement.style.display = "block";
      cardElement.style.zIndex = -9;
      cardElement.style.position = "absolute";
      cardElement.style.padding = 0;
      cardElement.style.backgroundColor = "transparent";
      await new Promise(resolve => setTimeout(resolve, 50));

      const canvas = await html2canvas(cardElement, { useCORS: true });
      const imageURL = canvas.toDataURL("image/png");

      const link = document.createElement("a");
      link.href = imageURL;
      link.download = `${value.providerId}-${value.employeeName}.png`;
      link.click();
    } catch (error) {
      console.error("Failed to download e-Card:", error);
    } finally {
      cardElement.style.display = originalStyle;
      setDownloadLoading(false);
      setDownloadItem(null);
      setModalIsOpen(false);
    }
  };

  const loadImage = src => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  const filterData = (cardFilter, memberFilter) => {
    let filtered = [...eCardsList];

    if (cardFilter !== "All") {
      filtered = filtered.filter(card => {
        const provider = card.providerName || card.providerId;
        return provider === cardFilter;
      });
    }

    if (memberFilter !== "All") {
      filtered = filtered.filter(card => card.memberName === memberFilter);
    }

    setFilteredCards(filtered);
  };

  const onFilterChanged = selectedOption => {
    const value = selectedOption.value || "All";
    setSelectedCardFilter(value);
    filterData(value, selectedMemberFilter);
  };

  const onMemberFilterChange = selectedOption => {
    const value = selectedOption.value || "All";
    setSelectedMemberFilter(value);
    filterData(selectedCardFilter, value);
  };

  const handleCloseModal = () => {
    setDownloadItem(null);
    setModalIsOpen(false);
  };

  // modal disclaimer
  const MessageModal = (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={handleCloseModal}
      className="message-board-modal"
      overlayClassName="message-board-modal-overlay"
    >
      <button onClick={handleCloseModal} className="link message-board-modal-close">
        &times;
      </button>
      <div>
        <h3>{intl.formatMessage(messages.claims.eCardsTitleDisclaimer)}</h3>
        <span>{intl.formatMessage(messages.claims.eCardsShareDisclaimer)}</span>
      </div>
      <div style={{ textAlign: "right", marginTop: "10px" }}>
        <Button primary type="button" className="download-button" onClick={handleCloseModal}>
          {intl.formatMessage(messages.cancelTextDescription)}
        </Button>
        <Button
          primary
          type="button"
          className="download-button"
          onClick={() => {
            if (
              [
                "RMG",
                "RmgSG",
                "AVIVA",
                "ParkwayShenton",
                "ParkWaySG",
                "Non-Nex",
                "Nex",
                "IHP",
                "ihp",
              ].includes(downloadItem.providerId)
            ) {
              handleCustomDownload(downloadItem);
            } else {
              handleDownload(downloadItem);
            }
          }}
          disabled={downloadLoading}
        >
          {downloadLoading ? "Loading..." : intl.formatMessage(messages.okTextDescription)}
        </Button>
      </div>
    </Modal>
  );

  // check if clinics menu are enable from admin portal // boolean
  const isClinicsAvailable = hasMenuWithId(
    menuItems,
    "FlexBenefitsMain_MyClaims",
    "FlexBenefitsMain_Clinics"
  );

  const handleShowClinic = value => {
    dispatch(setSelectedEcards(value.providerId));
    history.push("/claims/clinics");
  };

  const aiaCardWithButton = filteredCards.find(
    item => (item.providerId === "AIA" || item.providerName === "AIA") && item.buttonName
  );

  return (
    <Card>
      <CardSection>
        <TrackSsoUrlComponent getShortLivedToken={fetchShortLivedToken} />
        <h3>{intl.formatMessage(messages.claims.eCardsDescription)}</h3>
        <div className="claim-ecards-wrapper">
          <div className="claim-ecards-filter">
            <Select
              value={filters.find(filter => filter.value === selectedCardFilter)}
              defaultValue={selectedCardFilter}
              onChange={onFilterChanged}
              isClearable={false}
              options={filters}
            ></Select>
          </div>
        </div>
        <div className="claim-ecards-wrapper">
          <div className="claim-ecards-filter2">
            <Select
              value={filterMembers.find(filter => filter.value === selectedMemberFilter)}
              defaultValue={selectedMemberFilter}
              onChange={onMemberFilterChange}
              isClearable={false}
              options={filterMembers}
            ></Select>
          </div>
        </div>

        {aiaCardWithButton && (
          <div className="ecards-wrapper">
            <div className="display-ecards">
              <ClickOutside>
                <a
                  className="button button--primary download-button"
                  href={aiaCardWithButton.buttonURL}
                  target={"_blank"}
                  rel="noopener noreferrer"
                >
                  {intl.formatMessage({
                    id: aiaCardWithButton.buttonName,
                    defaultMessage: aiaCardWithButton.buttonName,
                  })}
                </a>
              </ClickOutside>
            </div>
          </div>
        )}

        {filteredCards.length > 0 ? (
          <Fragment>
            <div className="ecards-wrapper">
              {filteredCards.map(
                item =>
                  item.providerId !== "AIA" &&
                  item.providerName !== "AIA" && (
                    <Fragment key={item.cardId}>
                      <div className="display-ecards">
                        <ECardsItemLayout data={item} providerId={item.providerId} />
                        <div>
                          <ClickOutside>
                            <Button
                              primary
                              type="button"
                              onClick={() => {
                                setModalIsOpen(true);
                                setDownloadItem(item);
                              }}
                              className="download-button"
                            >
                              {intl.formatMessage({ id: "Download", defaultMessage: "Download" })}
                            </Button>
                          </ClickOutside>
                          {item.dataSource && isClinicsAvailable && (
                            <ClickOutside>
                              <Button
                                primary
                                type="button"
                                onClick={() => handleShowClinic(item)}
                                className="download-button"
                              >
                                {intl.formatMessage(messages.claims.eCardsShowClinic)}
                              </Button>
                            </ClickOutside>
                          )}
                        </div>
                      </div>

                      <div className="display-ecards">
                        <CustomDownloadEcardLayout data={item} providerId={item.providerId} />
                      </div>
                    </Fragment>
                  )
              )}
            </div>
            {MessageModal}
          </Fragment>
        ) : (
          <div className="ecards-not-found">
            <span>{intl.formatMessage(messages.claims.noECardsFoundDescription)}</span>
          </div>
        )}
      </CardSection>
    </Card>
  );
};
ECards.propTypes = {
  intl: intlShape.isRequired,
  history: PropTypes.object,
};
export default injectIntl(ECards);



// WEBPACK FOOTER //
// ./components/claim/ECards.js